import React, { useMemo } from 'react';
import { Box, Text, Title, Group, useMantineColorScheme } from '@mantine/core';
// react-syntax-highlighter: Before/After text diff panels with syntax highlighting and line-based diff colors.
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface TextDiffViewerProps {
    originalText: string;
    currentText: string;
    title?: string;
    language?: string; // For syntax highlighting (e.g., 'html', 'text')
}

/**
 * Simple text diff viewer for comparing plain text or HTML content
 * Shows side-by-side comparison with line-by-line highlighting
 */
export const TextDiffViewer: React.FC<TextDiffViewerProps> = ({
    originalText,
    currentText,
    title = 'Text Comparison',
    language = 'text'
}) => {
    const { colorScheme } = useMantineColorScheme();

    // Normalize line endings for comparison
    const normalizeText = (text: string): string => {
        if (typeof text !== 'string') return '';
        // Unescape if it's a JSON-escaped string
        const trimmed = text.trim();
        if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
            try {
                const parsed = JSON.parse(text);
                if (typeof parsed === 'string') {
                    return parsed.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                }
            } catch {
                // Not JSON-escaped, normalize line endings
                return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            }
        }
        return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    };

    const normalizedOriginal = normalizeText(originalText);
    const normalizedCurrent = normalizeText(currentText);

    // Split into lines for comparison
    const originalLines = normalizedOriginal.split('\n');
    const currentLines = normalizedCurrent.split('\n');

    // Compute which lines have changed
    const changedLines = useMemo(() => {
        const changed = new Set<number>();
        const maxLines = Math.max(originalLines.length, currentLines.length);

        for (let i = 0; i < maxLines; i++) {
            const originalLine = originalLines[i] || '';
            const currentLine = currentLines[i] || '';

            // Mark as changed if lines are different, or if one doesn't exist (added/removed)
            if (originalLine !== currentLine) {
                changed.add(i);
            } else if (i >= originalLines.length && i < currentLines.length) {
                // Line added
                changed.add(i);
            } else if (i >= currentLines.length && i < originalLines.length) {
                // Line removed
                changed.add(i);
            }
        }

        return changed;
    }, [originalLines, currentLines]);

    // Simple line-by-line comparison with highlighting
    const getLineStyle = (lineNumber: number, isAfter: boolean) => {
        const lineIndex = lineNumber - 1; // Convert to 0-based index (lineNumber is 1-based)
        const isDark = colorScheme === 'dark';
        const isChanged = changedLines.has(lineIndex);
        const originalLine = originalLines[lineIndex] || '';
        const currentLine = currentLines[lineIndex] || '';

        if (!isChanged) {
            return {};
        }

        let backgroundColor = '';
        if (isAfter) {
            // In "after" pane
            if (lineIndex >= originalLines.length) {
                // New line added
                backgroundColor = isDark ? '#2e7d32' : '#e8f5e8';
            } else {
                // Line changed
                backgroundColor = isDark ? '#8b6914' : '#fff3cd';
            }
        } else {
            // In "before" pane
            if (lineIndex >= currentLines.length) {
                // Line removed
                backgroundColor = isDark ? '#c62828' : '#ffeaea';
            } else {
                // Line changed
                backgroundColor = isDark ? '#8b6914' : '#fff3cd';
            }
        }

        return {
            style: {
                display: 'block',
                backgroundColor: backgroundColor,
                width: '100%',
                paddingLeft: '4px',
                marginLeft: '-4px'
            }
        };
    };

    return (
        <Box pt="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <Title order={5} mb="sm">
                {title}
            </Title>

            <Group gap="md" align="stretch" wrap="nowrap" style={{ overflow: 'hidden' }}>
                <Box flex={1} miw={0}>
                    <Text size="sm" c="blue" mb="sm">
                        Before (Original)
                    </Text>
                    <Box
                        style={{
                            border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 4,
                            overflow: 'hidden'
                        }}
                    >
                        <SyntaxHighlighter
                            language={language}
                            style={colorScheme === 'dark' ? vscDarkPlus : vs}
                            customStyle={{
                                margin: 0,
                                borderRadius: '4px',
                                maxHeight: '400px',
                                overflow: 'auto',
                                overflowX: 'auto',
                                whiteSpace: 'pre',
                                wordBreak: 'normal'
                            }}
                            lineProps={(lineNumber) => getLineStyle(lineNumber, false)}
                            showLineNumbers={true}
                            wrapLines={false}
                        >
                            {normalizedOriginal || '(empty)'}
                        </SyntaxHighlighter>
                    </Box>
                </Box>

                <Box flex={1} miw={0}>
                    <Text size="sm" c="dimmed" mb="sm">
                        After (Current)
                    </Text>
                    <Box
                        style={{
                            border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 4,
                            overflow: 'hidden'
                        }}
                    >
                        <SyntaxHighlighter
                            language={language}
                            style={colorScheme === 'dark' ? vscDarkPlus : vs}
                            customStyle={{
                                margin: 0,
                                borderRadius: '4px',
                                maxHeight: '400px',
                                overflow: 'auto',
                                overflowX: 'auto',
                                whiteSpace: 'pre',
                                wordBreak: 'normal'
                            }}
                            lineProps={(lineNumber) => getLineStyle(lineNumber, true)}
                            showLineNumbers={true}
                            wrapLines={false}
                        >
                            {normalizedCurrent || '(empty)'}
                        </SyntaxHighlighter>
                    </Box>
                </Box>
            </Group>
        </Box>
    );
};
